import { memo, MouseEventHandler, ReactNode, useEffect, useState } from "react";

import { Button, CircularProgress } from "@mui/material";

/**
 * Props for Major Button Component.
 * @interface
 * @name MajorButtonComponentProps
 * @kind interface
 */
interface MajorButtonComponentProps {
  /**
   * Type of Button.
   * @default "primary"
   * @property
   * @name buttonType
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {"primary" | "secondary"}
   */
  buttonType: "primary" | "secondary";
  /**
   * Children Component (`ReactNode`).
   * @property
   * @name children
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {ReactNode}
   */
  children: ReactNode;
  /**
   * Id for the Major Button Component.
   * @property
   * @name id
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {string}
   */
  id: string;
  /**
   * Standard HTML Attribute.
   * @property
   * @name type
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {"button" | "submit" | "reset" | undefined}
   */
  type: "button" | "submit" | "reset" | undefined;
  /**
   * (optional)
   * Standard HTML Attribute.
   * If `true`, the component is disabled.
   * @default false
   * @property
   * @name disabled
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {boolean | undefined}
   */
  disabled?: boolean;
  /**
   * (optional)
   * If `true`, shows the loading spinner.
   * @default false
   * @property
   * @name isLoading
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {boolean | undefined}
   */
  isLoading?: boolean;
  /**
   * (optional)
   * Callback fired when the popup requests to be closed.
   * @property
   * @name onClick
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {MouseEventHandler | undefined}
   */
  onClick?: MouseEventHandler | undefined;
  /**
   * (optional)
   * Size of the component.
   * `small` is equivalent to the dense button styling.
   * @default 'medium'
   * @property
   * @name size
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {"small" | "medium" | "large" | undefined}
   */
  size?: "small" | "medium" | "large";
  /**
   * (optional)
   * Standard HTML Attribute, for classes.
   * @property
   * @name className
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {string | undefined}
   */
  className?: string;
  /**
   * (optional)
   * If `true`, shows the wider button.
   * @default false
   * @property
   * @name widerButton
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {boolean | undefined}
   */
  widerButton?: boolean;
  /**
   * (optional)
   * If `true`, shows the icon.
   * @default false
   * @property
   * @name hasIcon
   * @kind property
   * @memberof MajorButtonComponentProps
   * @type {boolean | undefined}
   */
  hasIcon?: boolean;
}

/**
 * Memoized Major Button Component.
 * @constant
 * @name MajorButtonComponent
 * @kind variable
 * @type {React.NamedExoticComponent<MajorButtonComponentProps>}
 */
const MajorButtonComponent: React.NamedExoticComponent<MajorButtonComponentProps> =
  memo(
    /**
     * Major Button Component.
     * @function
     * @name MajorButtonComponent
     * @kind function
     * @memberof MajorButtonComponent
     * @param {MajorButtonComponentProps} props Props for Major Button Component.
     * @returns {JSX.Element} Major Button Component.
     */
    function MajorButtonComponent(
      props: MajorButtonComponentProps
    ): JSX.Element {
      const {
        buttonType,
        children,
        hasIcon,
        id,
        type,
        className,
        disabled,
        isLoading,
        onClick,
        size,
        widerButton,
      } = props;

      const [buttonColor, setButtonColor] = useState<string>("");
      const [textColor, setTextColor] = useState<string>("");

      function mapButtonTypeToColor(): void {
        switch (buttonType) {
          case "primary":
          default:
            setButtonColor(
              "bg-red-600 hover:bg-red-700 disabled:bg-zinc-100 disabled:text-stone-300"
            );
            setTextColor("text-white");
            break;
          case "secondary":
            setButtonColor(
              "border border-solid bg-white border-neutral-400 hover:bg-zinc-100 disabled:border-gray-300 disabled:text-gray-300"
            );
            setTextColor("text-zinc-600");
            break;
        }
      }

      useEffect(() => mapButtonTypeToColor());

      return (
        <Button
          disabled={disabled}
          type={type}
          id={id}
          className={`gap-x-3 rounded-lg normal-case ${
            hasIcon ? "" : "px-8 py-3"
          } ${
            widerButton ? "" : "w-52"
          } ${buttonColor} ${textColor} ${className}`}
          size={size}
          onClick={onClick}
        >
          {children}
          {isLoading ? (
            <CircularProgress className="text-primary-100" size={"16px"} />
          ) : (
            ""
          )}
        </Button>
      );
    }
  );

export default MajorButtonComponent;
